import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm, FormInstance } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";
import UploadImage from "../../../components/UploadImage";

interface Step {
  title: string;
  description: string;
}

interface OurProcessProps {
  heading?: string;
  subtext?: string;
  steps?: Step[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: "Our Process") => void;
  form: FormInstance;
  ourProcessData?: OurProcessProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const OurProcess: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  ourProcessData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!ourProcessData) return;

    if (mode === "list") {
      form.setFieldsValue({
        our_process: {
          heading: ourProcessData.heading ?? "",
          subtext: ourProcessData.subtext ?? "",
          steps: ourProcessData.steps ?? [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        our_process: {
          steps: [{ title: "", description: "" }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = ourProcessData.steps?.[editingIndex];
      if (item) {
        form.setFieldsValue({
          our_process: {
            steps: [item],
          },
        });
      }
    }
  }, [mode, editingIndex, ourProcessData, form]);

  const steps = ourProcessData?.steps ?? [];

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["our_process", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="Enter heading..." />
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["our_process", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} placeholder="Enter subtext..." />
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="bg-[#F9F9F9]">
                {ourProcessData?.heading ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">{ourProcessData.heading}</td>
                    <td className="px-4 py-3">{ourProcessData.subtext}</td>
                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}

                <tr>
                  {steps?.length < 3 && (
                    <td colSpan={4} className="px-4 py-3 text-right">
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setMode("add");
                          setEditingIndex(null);
                        }}
                      >
                        Add Steps
                      </Button>
                    </td>
                  )}
                </tr>

                <tr>
                  <td colSpan={4} className="p-4">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Title</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {steps?.length ? (
                          steps?.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">{item.title}</td>
                              <td className="px-4 py-2">{item.description}</td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />
                                  <Popconfirm
                                    title="Delete this highlight?"
                                    onConfirm={() =>
                                      onDelete(index, "Our Process")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              className="text-center py-6 text-gray-500"
                            >
                              No steps added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-4">
            {ourProcessData?.heading ? (
              <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm">
                <span className="text-primary font-semibold">Heading: </span>
                <p className="font-medium">{ourProcessData.heading}</p>
                <span className="text-primary font-semibold">Subtext: </span>
                <p className="">{ourProcessData.subtext}</p>

                <div>
                  <span className="font-semibold text-primary block mt-2 mb-1">
                    Action:
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-black text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}

            {steps?.length ? (
              steps?.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm"
                >
                  <p>
                    <span className="text-primary font-semibold">Title:</span>{" "}
                    {item.title}
                  </p>
                  <p>
                    <span className="text-primary font-semibold">
                      Description:
                    </span>{" "}
                    {item.description}
                  </p>
                  <div className="flex justify-end gap-3 mt-2">
                    <EditOutlined
                      className="cursor-pointer text-primary"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />
                    <Popconfirm
                      title="Delete this highlight?"
                      onConfirm={() => onDelete(index, "Our Process")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No steps added
              </div>
            )}
            {steps?.length < 3 && (
              <div className="text-right">
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setMode("add");
                    setEditingIndex(null);
                  }}
                >
                  Add Steps
                </Button>
              </div>
            )}
            
          </div>

        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["our_process", "steps"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[{ required: true, message: "Enter title" }]}
                      >
                        <Input placeholder="Enter title..." />
                      </Form.Item>

                      <Form.Item
                        label="Description"
                        name={[field.name, "description"]}
                        rules={[
                          {
                            required: true,
                            message: "Enter description",
                          },
                        ]}
                      >
                        <TextArea rows={3} placeholder="Enter description..." />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default OurProcess;
